import React, { useEffect, useState, useCallback } from 'react';
import { getBatches, getBatchResults, reprocessBatch, reprocessBatchLine, resendAllBatchLines, pauseBatchApi, stopBatchApi, resumeBatchApi, nudgeBatchApi, deleteBatch } from '../services/api';
import ScoreLegend from './ScoreLegend';
import MatchDetails from './MatchDetails';

const STATUS_COLORS = {
  NEW:             'received',
  WIP:             'processing',
  FETCHING_DATA:   'processing',
  PROCESSING:      'processing',
  LINE_PROCESSING: 'processing',
  PAUSED:          'paused',
  COMPLETE:        'completed',
  FAILED:          'failed',
};

const STATUS_LABELS = {
  NEW:             'New',
  WIP:             'WIP',
  FETCHING_DATA:   'Fetching',
  PROCESSING:      'Processing',
  LINE_PROCESSING: 'Matching',
  PAUSED:          'Paused',
  COMPLETE:        'Complete',
  FAILED:          'Failed',
};

function fmtDateTime(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60)  return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function buildBatchShareUrl(batchId, expId) {
  const base = `${window.location.origin}${window.location.pathname}`;
  return `${base}?view=share&batch=${encodeURIComponent(batchId)}&exp=${encodeURIComponent(expId)}`;
}

function buildBatchAllShareUrl(batchId) {
  const base = `${window.location.origin}${window.location.pathname}`;
  return `${base}?view=share&batch=${encodeURIComponent(batchId)}`;
}

function fmtS(val) {
  if (val == null) return <span style={{ color: 'var(--text-muted)' }}>-</span>;
  const n = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(n)) return <span style={{ color: 'var(--text-muted)' }}>-</span>;
  const display = Number.isInteger(n) ? n.toString() : n.toFixed(2);
  return <span style={{ fontWeight: n > 0 ? 700 : 400, color: n > 0 ? 'var(--green)' : 'var(--text-muted)' }}>{display}</span>;
}

function getRuleLabel(award) {
  const hasName    = (award.name_match_score    || 0) > 0;
  const hasPurpose = (award.purpose_match_score || 0) > 0;
  const hasProgram = (award.program_match_score || 0) > 0;
  if (hasName && hasPurpose && hasProgram) return 'N+P+Prg';
  if (hasName && (hasPurpose || hasProgram)) return `N+${hasPurpose ? 'P' : 'Prg'}`;
  if (hasName)    return 'Name';
  if (hasPurpose && hasProgram) return 'P+Prg';
  if (hasPurpose) return 'Purpose';
  if (hasProgram) return 'Program';
  return 'Semantic';
}

// ── Expenditure result row (inside expanded batch) ────────────────────────────
function ExpRow({ lineResult, lineNumber, batchId, batchStatus, mode, onReprocess }) {
  const [open, setOpen]               = useState(false);
  const [expandedAward, setExpAward]  = useState(null);
  const [copied, setCopied]           = useState(false);
  const [showOrds, setShowOrds]       = useState(false);
  const [copiedOrds, setCopiedOrds]   = useState(false);
  const [reprocessing, setReprocess]  = useState(false);
  const [reprocessErr, setReprocessErr] = useState(null);

  const { expenditure: exp, matched_awards: matched = [], unmatched_awards: unmatched = [],
    expenditure_matched, ords_notified, ords_notify_error, unmatched_expenditure_reason,
    ords_payload } = lineResult;

  const canReprocess = ['COMPLETE', 'FAILED'].includes(batchStatus);

  async function handleReprocess(e) {
    e.stopPropagation();
    if (!canReprocess || reprocessing) return;
    setReprocess(true);
    setReprocessErr(null);
    try {
      await reprocessBatchLine(batchId, exp.exp_id);
      // Poll until the line result updates (server processes async)
      let attempts = 0;
      const poll = setInterval(() => {
        attempts++;
        if (attempts > 30) { clearInterval(poll); setReprocess(false); return; }
        getBatchResults(batchId)
          .then(d => {
            const updated = d.results?.find(r => r.expenditure?.exp_id === exp.exp_id);
            if (updated?.completed_at !== lineResult.completed_at) {
              clearInterval(poll);
              setReprocess(false);
              onReprocess && onReprocess(d);
            }
          })
          .catch(() => {});
      }, 2000);
    } catch (err) {
      setReprocessErr(err.response?.data?.error || err.message);
      setReprocess(false);
    }
  }
  const topMatch = matched.find(a => a.rank === 1);
  // candidate_count = full pool (stored on result); fallback to matched+unmatched for old records
  const totalCandidates = lineResult.candidate_count ?? (matched.length + unmatched.length);
  const shareUrl = buildBatchShareUrl(batchId, exp.exp_id);

  function copyShare(e) {
    e.stopPropagation();
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      {/* Expenditure header row */}
      <div
        className={`batch-exp-row${open ? ' batch-exp-row--open' : ''}`}
        style={{
          background: open ? 'rgba(139,0,0,0.07)' : 'transparent',
          borderLeft: open ? '3px solid rgba(139,0,0,0.35)' : '3px solid transparent',
        }}
        tabIndex={0}
        role="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
      >
        {lineNumber != null && (
          <span style={{ fontSize: 10, color: 'var(--text-muted)', minWidth: 24, textAlign: 'right', flexShrink: 0 }}>
            {lineNumber}
          </span>
        )}
        <span style={{ fontSize: 11, color: 'var(--text-muted)', width: 12 }}>
          {open ? '▼' : '▶'}
        </span>
        <span className="mono" style={{ fontSize: 11, fontWeight: 700, minWidth: 120 }}>
          {exp.exp_id}
        </span>
        {exp.amt != null && (
          <span style={{ fontSize: 11, color: 'var(--accent-hover)', fontWeight: 600, minWidth: 70 }}>
            ${Number(exp.amt).toLocaleString()}
          </span>
        )}
        {/* Category + type + org/project */}
        <span style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {exp.exp_cat || exp.exp_txn_desc || '—'}
            {exp.exp_type && (
              <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-muted)', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 3, padding: '0 4px' }}>
                {exp.exp_type}
              </span>
            )}
          </span>
          {(exp.task_org_name || exp.proj_name || exp.awd_num) && (
            <span style={{ fontSize: 10, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {exp.task_org_name && <span>Org: <strong style={{ color: 'var(--text)' }}>{exp.task_org_name}</strong></span>}
              {exp.task_org_name && exp.proj_name && <span style={{ margin: '0 5px' }}>·</span>}
              {exp.proj_name && <span>Proj: <strong style={{ color: 'var(--text)' }}>{exp.proj_name}</strong></span>}
              {exp.awd_num && <span style={{ marginLeft: 5 }}>· Hint: <span className="mono" style={{ color: 'var(--accent-hover)' }}>{exp.awd_num}</span></span>}
            </span>
          )}
        </span>
        {expenditure_matched ? (
          <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {matched.length} match{matched.length !== 1 ? 'es' : ''}
            {topMatch && (
              <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 5 }}>
                · Top: <span className="mono" style={{ color: 'var(--accent-hover)' }}>{topMatch.awd_num}</span>
                {' '}({topMatch.total_score?.toFixed(1)})
              </span>
            )}
            <span style={{ color: 'var(--text-muted)', marginLeft: 5 }}>· {totalCandidates} candidates</span>
          </span>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--red)', fontWeight: 600 }}>No match</span>
        )}
        {ords_notified ? (
          <span style={{ fontSize: 10, color: 'var(--green)', border: '1px solid var(--green)',
            borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }} title="ORDS notified successfully">
            ORDS ✓
          </span>
        ) : ords_notify_error ? (
          <span style={{ fontSize: 10, color: 'var(--red)', border: '1px solid var(--red)',
            borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }} title={`ORDS notify failed: ${ords_notify_error}`}>
            ORDS ✗
          </span>
        ) : null}
        <span style={{ display: 'flex', gap: 4, marginLeft: 4, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
          <a
            href={shareUrl} target="_blank" rel="noopener noreferrer"
            className="detail-toggle-btn"
            style={{ fontSize: 10, padding: '2px 7px', textDecoration: 'none' }}
            title="Open in new tab"
          >
            View ↗
          </a>
          <button
            className="detail-toggle-btn"
            style={{ fontSize: 10, padding: '2px 7px' }}
            onClick={copyShare}
            title="Copy share link"
          >
            {copied ? '✓' : '🔗'}
          </button>
          {canReprocess && (
            <button
              className="detail-toggle-btn"
              style={{
                fontSize: 10, padding: '2px 7px',
                color: reprocessing ? 'var(--text-muted)' : 'var(--orange)',
                borderColor: 'var(--orange)', opacity: reprocessing ? 0.6 : 1,
                cursor: reprocessing ? 'not-allowed' : 'pointer',
              }}
              disabled={reprocessing}
              onClick={handleReprocess}
              title="Reprocess and resend this line to ORDS"
            >
              {reprocessing ? '⟳ Sending…' : '↺ Resend'}
            </button>
          )}
          {reprocessErr && (
            <span style={{ fontSize: 10, color: 'var(--red)', maxWidth: 120,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={reprocessErr}
            >
              {reprocessErr}
            </span>
          )}
        </span>
      </div>

      {/* Expanded body: expenditure detail + matched awards table */}
      {open && (
        <div style={{ padding: '0 12px 12px 32px', background: 'var(--bg-card)' }}>
          {/* Expenditure metadata */}
          <div className="kv-grid" style={{ fontSize: 11, marginBottom: 8, marginTop: 6 }}>
            {exp.task_org_name && <KV label="Org"         value={exp.task_org_name} />}
            {exp.proj_name     && <KV label="Project"     value={exp.proj_name} />}
            {exp.task_name     && <KV label="Task"        value={exp.task_name} />}
            {exp.exp_txn_long_desc && (
              <div className="kv-row" style={{ gridColumn: '1 / -1' }}>
                <span className="kv-label">Description</span>
                <span className="kv-value" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11 }}>
                  {exp.exp_txn_long_desc}
                </span>
              </div>
            )}
          </div>

          {/* Matched awards table */}
          {matched.length > 0 && (
            <div className="data-table-wrap" style={{ marginTop: 6 }}>
              <table className="data-table compact" style={{ fontSize: 11 }}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Award</th>
                    <th>Name</th>
                    <th>Nm</th>
                    <th>Pur</th>
                    <th>Prg</th>
                    <th>Rst</th>
                    <th>Age</th>
                    <th>{mode === 'vector' ? 'Vec' : mode === 'hybrid' ? 'Hyb' : mode === 'vector-llm' ? 'V+L' : 'Sem'}</th>
                    <th>Total</th>
                    <th>Rule</th>
                  </tr>
                </thead>
                <tbody>
                  {matched.map(a => (
                    <React.Fragment key={a.awd_num}>
                      <tr
                        style={{ cursor: 'pointer' }}
                        className={expandedAward === a.awd_num ? 'row-expanded' : ''}
                        onClick={() => setExpAward(expandedAward === a.awd_num ? null : a.awd_num)}
                      >
                        <td>
                          <span className={`rank-dot ${a.rank === 1 ? 'gold' : a.rank === 2 ? 'silver' : a.rank === 3 ? 'bronze' : 'default'}`}>
                            {a.rank}
                          </span>
                        </td>
                        <td className="mono" style={{ fontWeight: 600 }}>{a.awd_num}</td>
                        <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.awd_name}
                        </td>
                        <td>{fmtS(a.name_match_score)}</td>
                        <td>{fmtS(a.purpose_match_score)}</td>
                        <td>{fmtS(a.program_match_score)}</td>
                        <td>{fmtS(a.restriction_score)}</td>
                        <td>{fmtS(a.age_score)}</td>
                        <td>{fmtS(a.semantic_score)}</td>
                        <td style={{ fontWeight: 800, color: 'var(--accent-hover)', whiteSpace: 'nowrap' }}>
                          {a.total_score?.toFixed(2)}
                          {a.confidence && (
                            <span className={`confidence-badge ${a.confidence}`} style={{ marginLeft: 4 }}>
                              {a.confidence}
                            </span>
                          )}
                        </td>
                        <td style={{ fontSize: 10, color: 'var(--text-muted)' }}>{getRuleLabel(a)}</td>
                      </tr>
                      {expandedAward === a.awd_num && (
                        <tr className="expanded-row">
                          <td colSpan={11}>
                            <div className="expanded-detail" style={{ fontSize: 11 }}>
                              <div className="kv-grid" style={{ fontSize: 11 }}>
                                <KV label="Organization" value={a.awd_org} />
                                <KV label="Start Date"   value={a.awd_start} />
                                <KV label="Purpose"      value={a.awd_purpose} />
                                <KV label="Restriction"  value={a.awd_restrict} />
                                <KV label="Control"      value={a.awd_ctrl} />
                                <KV label="Employee"     value={a.awd_role_emp} />
                                <KV label="Person (ORDS)" value={a.awd_person} />
                                <KV label="Purpose (ORDS)" value={a.awd_purpose_desc} />
                                <KV label="Program (ORDS)" value={a.awd_program} />
                              </div>
                              {a.awd_fund_auth && (
                                <div style={{ marginTop: 5, color: 'var(--text-muted)' }}>
                                  <strong>Fund Auth:</strong> {a.awd_fund_auth}
                                </div>
                              )}
                              {a.match_reasons?.length > 0 && (
                                <div style={{ marginTop: 6 }}>
                                  <div className="match-evidence-header">Match Evidence</div>
                                  <ol className="match-reasons-list">
                                    {a.match_reasons.map((r, i) => (
                                      <li key={i} className="match-reason-item">{r}</li>
                                    ))}
                                  </ol>
                                </div>
                              )}
                              <MatchDetails details={a.match_details} />
                              {a.matched_fields?.length > 0 && (
                                <div className="matched-fields-list" style={{ marginTop: 5 }}>
                                  {a.matched_fields.map((f, i) => <span key={i} className="field-tag">{f}</span>)}
                                </div>
                              )}
                              {a.explanation && (
                                <div style={{ marginTop: 5, fontStyle: 'italic', color: 'var(--text-muted)' }}>
                                  <strong style={{ fontStyle: 'normal', color: 'var(--accent-hover)' }}>AI:</strong> {a.explanation}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!expenditure_matched && (
            <div className="warning-box" style={{ padding: '5px 10px', fontSize: 11, marginTop: 6 }}>
              No matching awards found for this expenditure.
              {unmatched_expenditure_reason && <span> {unmatched_expenditure_reason}</span>}
            </div>
          )}
          {totalCandidates > 0 && matched.length < totalCandidates && (
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
              {matched.length} of {totalCandidates} candidates matched.
            </div>
          )}

          {/* ORDS payload viewer */}
          {ords_payload && (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <button
                  className="detail-toggle-btn"
                  style={{ fontSize: 10, padding: '2px 8px' }}
                  onClick={() => setShowOrds(o => !o)}
                >
                  {showOrds ? '▼' : '▶'} ORDS Payload {ords_notified ? '✓' : ords_notify_error ? '✗' : ''}
                </button>
                {showOrds && (
                  <button
                    className="detail-toggle-btn"
                    style={{ fontSize: 10, padding: '2px 8px' }}
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(ords_payload, null, 2)).then(() => {
                        setCopiedOrds(true);
                        setTimeout(() => setCopiedOrds(false), 2000);
                      });
                    }}
                  >
                    {copiedOrds ? '✓ Copied' : '📋 Copy JSON'}
                  </button>
                )}
                {ords_notify_error && (
                  <span style={{ fontSize: 10, color: 'var(--red)' }} title={ords_notify_error}>
                    ORDS error: {ords_notify_error.slice(0, 60)}{ords_notify_error.length > 60 ? '…' : ''}
                  </span>
                )}
              </div>
              {showOrds && (
                <pre style={{
                  fontSize: 10, margin: 0, padding: '8px 10px',
                  background: 'var(--bg)', border: '1px solid var(--border)',
                  borderRadius: 5, overflowX: 'auto', maxHeight: 300,
                  color: 'var(--text-muted)', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                }}>
                  {JSON.stringify(ords_payload, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Batch detail panel (expanded row) ────────────────────────────────────────
function BatchDetail({ batch, onReprocess }) {
  const [view, setView]               = useState('results'); // 'results' | 'usage' | 'history'
  const [results, setResults]         = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [reprocessing, setReprocess]  = useState(false);
  const [reprocessErr, setReprocessErr] = useState(null);
  const [resendingAll, setResendingAll] = useState(false);
  const [resendAllErr, setResendAllErr] = useState(null);
  const [copiedBatchLink, setCopiedBatchLink] = useState(false);
  const [controlMsg, setControlMsg]   = useState(null);
  const [nudging, setNudging]         = useState(false);
  const [nudgeMsg, setNudgeMsg]       = useState(null);
  const [nudgePolling, setNudgePolling] = useState(false);

  // Pagination
  const [expPage, setExpPage]         = useState(0);
  const [expPageSize, setExpPageSize] = useState(10);

  const canReprocess  = ['COMPLETE', 'FAILED'].includes(batch.status);
  const isInProgress  = ['WIP', 'FETCHING_DATA', 'PROCESSING', 'LINE_PROCESSING'].includes(batch.status);
  const isPaused      = batch.status === 'PAUSED';
  // hasMismatch: COMPLETE batch where some lines have no result
  const hasMismatch   = results != null && batch.status === 'COMPLETE'
    && results.lines_complete < results.lines_total;
  const canNudge      = ['LINE_PROCESSING', 'PROCESSING', 'PAUSED', 'FAILED'].includes(batch.status) || hasMismatch;

  function loadResults() {
    return getBatchResults(batch.batch_id)
      .then(d => { setResults(d); return d; })
      .catch(e => setError(e.response?.data?.error || e.message));
  }

  useEffect(() => {
    if (view !== 'results' && view !== 'usage') return;
    setLoading(true);
    setError(null);
    loadResults().finally(() => setLoading(false));
  }, [view, batch.batch_id]);

  async function handleReprocessBatch() {
    if (!canReprocess || reprocessing) return;
    setReprocess(true);
    setReprocessErr(null);
    try {
      await onReprocess(batch.batch_id, { stopPropagation: () => {} });
    } catch (err) {
      setReprocessErr(err.response?.data?.error || err.message);
    } finally {
      setReprocess(false);
    }
  }

  async function handleResendAll() {
    if (!canReprocess || resendingAll) return;
    setResendingAll(true);
    setResendAllErr(null);
    try {
      await resendAllBatchLines(batch.batch_id);
      // Poll results until all line completed_at timestamps have advanced past the request time
      const startedAt = Date.now();
      let attempts = 0;
      const poll = setInterval(() => {
        attempts++;
        if (attempts > 90) { clearInterval(poll); setResendingAll(false); return; } // 3 min max
        getBatchResults(batch.batch_id).then(d => {
          setResults(d);
          // Stop polling once all completed_at are newer than when we started
          const allFresh = (d.results || []).every(r =>
            r.completed_at && new Date(r.completed_at).getTime() >= startedAt
          );
          if (allFresh && d.results?.length > 0) {
            clearInterval(poll);
            setResendingAll(false);
          }
        }).catch(() => {});
      }, 2000);
    } catch (err) {
      setResendAllErr(err.response?.data?.error || err.message);
      setResendingAll(false);
    }
  }

  function handleCopyBatchLink() {
    const url = buildBatchAllShareUrl(batch.batch_id);
    navigator.clipboard.writeText(url).then(() => {
      setCopiedBatchLink(true);
      setTimeout(() => setCopiedBatchLink(false), 2000);
    });
  }

  async function handlePause() {
    setControlMsg(null);
    try {
      await pauseBatchApi(batch.batch_id);
      setControlMsg({ type: 'info', text: 'Pause signal sent — will pause after current chunk' });
    } catch (err) {
      setControlMsg({ type: 'error', text: err.response?.data?.error || err.message });
    }
  }

  async function handleStop() {
    setControlMsg(null);
    try {
      await stopBatchApi(batch.batch_id);
      setControlMsg({ type: 'info', text: 'Stop signal sent — will finalise as COMPLETE and notify ORDS' });
    } catch (err) {
      setControlMsg({ type: 'error', text: err.response?.data?.error || err.message });
    }
  }

  async function handleResume() {
    setControlMsg(null);
    try {
      await resumeBatchApi(batch.batch_id);
      setControlMsg({ type: 'info', text: 'Resuming batch processing…' });
    } catch (err) {
      setControlMsg({ type: 'error', text: err.response?.data?.error || err.message });
    }
  }

  async function handleNudge() {
    if (nudging) return;
    setNudgeMsg(null);
    setNudging(true);
    try {
      const resp = await nudgeBatchApi(batch.batch_id);
      const remaining = resp.remaining ?? '?';
      const done = resp.done ?? '?';
      setNudgeMsg({
        type: 'info',
        text: `Nudged — processing ${remaining} missing line(s)…`,
      });
      // For COMPLETE batches with a mismatch, poll results until all lines fill in
      if (hasMismatch) setNudgePolling(true);
    } catch (err) {
      const status = err.response?.status;
      const msg    = err.response?.data?.error || err.message;
      if (status === 409) {
        setNudgeMsg({ type: 'warn', text: `Already processing — ${msg}` });
      } else {
        setNudgeMsg({ type: 'error', text: msg });
      }
    } finally {
      setNudging(false);
    }
  }

  // Auto-refresh while batch is processing OR while nudge is filling in missing lines
  useEffect(() => {
    if (view !== 'results') return;
    const inProgress = ['NEW', 'WIP', 'FETCHING_DATA', 'PROCESSING', 'LINE_PROCESSING', 'PAUSED'].includes(batch.status);
    if (!inProgress && !nudgePolling) return;
    const id = setInterval(() => {
      getBatchResults(batch.batch_id).then(d => {
        setResults(d);
        // Stop polling once all missing lines have results
        if (nudgePolling && d.lines_complete >= d.lines_total) {
          setNudgePolling(false);
          setNudgeMsg({ type: 'info', text: `All ${d.lines_total} lines now complete.` });
        }
      }).catch(() => {});
    }, 4000);
    return () => clearInterval(id);
  }, [view, batch.batch_id, batch.status, nudgePolling]);

  return (
    <div style={{ padding: '0 0 12px 0', background: 'var(--bg-card)', borderTop: '1px solid var(--border)' }}>
      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 10, alignItems: 'center' }}>
        {['results', 'usage', 'history'].map(v => (
          <button
            key={v}
            onClick={() => setView(v)}
            style={{
              padding: '6px 16px', fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer',
              background: view === v ? 'var(--bg)' : 'transparent',
              color: view === v ? 'var(--accent-hover)' : 'var(--text-muted)',
              borderBottom: view === v ? '2px solid var(--accent-hover)' : '2px solid transparent',
            }}
          >
            {v === 'results' ? 'Expenditure Results' : v === 'usage' ? 'Usage' : 'State History'}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', padding: '4px 12px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>

          {/* ── Pause / Stop / Nudge controls (shown while batch is actively processing) ── */}
          {isInProgress && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={handlePause}
                  style={{
                    padding: '3px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                    border: '1px solid var(--text-muted)', background: 'rgba(100,100,100,0.1)',
                    color: 'var(--text-muted)', cursor: 'pointer',
                  }}
                  title="Pause processing after the current chunk — batch can be resumed"
                >
                  ⏸ Pause
                </button>
                <button
                  onClick={handleStop}
                  style={{
                    padding: '3px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                    border: '1px solid var(--red)', background: 'rgba(239,68,68,0.1)',
                    color: 'var(--red)', cursor: 'pointer',
                  }}
                  title="Stop processing and mark batch COMPLETE — notifies ORDS with partial results"
                >
                  ⏹ Stop
                </button>
                {canNudge && (
                  <button
                    onClick={handleNudge}
                    disabled={nudging}
                    style={{
                      padding: '3px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                      border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.1)',
                      color: '#f59e0b', cursor: nudging ? 'not-allowed' : 'pointer',
                      opacity: nudging ? 0.6 : 1,
                    }}
                    title="Stuck? Nudge resumes processing from the last completed expenditure"
                  >
                    {nudging ? '⟳ Nudging…' : '⚡ Nudge'}
                  </button>
                )}
              </div>
              {(controlMsg || nudgeMsg) && (
                <span style={{
                  fontSize: 10,
                  color: (nudgeMsg || controlMsg).type === 'error' ? 'var(--red)'
                       : (nudgeMsg || controlMsg).type === 'warn'  ? '#f59e0b'
                       : 'var(--text-muted)',
                }}>
                  {(nudgeMsg || controlMsg).text}
                </span>
              )}
            </div>
          )}

          {/* ── Resume / Nudge controls (shown when batch is PAUSED) ── */}
          {isPaused && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={handleResume}
                  style={{
                    padding: '3px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                    border: '1px solid var(--green)', background: 'rgba(16,185,129,0.1)',
                    color: 'var(--green)', cursor: 'pointer',
                  }}
                  title="Resume processing remaining lines from where it was paused"
                >
                  ▶ Resume
                </button>
                <button
                  onClick={handleNudge}
                  disabled={nudging}
                  style={{
                    padding: '3px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                    border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.1)',
                    color: '#f59e0b', cursor: nudging ? 'not-allowed' : 'pointer',
                    opacity: nudging ? 0.6 : 1,
                  }}
                  title="Nudge: skip already-processed lines and resume from the next unprocessed expenditure"
                >
                  {nudging ? '⟳ Nudging…' : '⚡ Nudge'}
                </button>
              </div>
              {(nudgeMsg || controlMsg) && (
                <span style={{
                  fontSize: 10,
                  color: (nudgeMsg || controlMsg).type === 'error' ? 'var(--red)'
                       : (nudgeMsg || controlMsg).type === 'warn'  ? '#f59e0b'
                       : 'var(--text-muted)',
                }}>
                  {(nudgeMsg || controlMsg).text}
                </span>
              )}
            </div>
          )}

          {/* Share whole batch link */}
          <button
            className="detail-toggle-btn"
            onClick={handleCopyBatchLink}
            title="Copy a shareable link to all batch results"
            style={{ fontSize: 10, padding: '3px 10px' }}
          >
            {copiedBatchLink ? '✓ Copied!' : '🔗 Share Batch'}
          </button>

          {canReprocess && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {/* Resend all lines to ORDS */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                  <button
                    disabled={resendingAll || reprocessing}
                    onClick={handleResendAll}
                    style={{
                      padding: '3px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                      border: '1px solid var(--accent-hover)', background: 'rgba(99,102,241,0.1)',
                      color: 'var(--accent-hover)',
                      cursor: (resendingAll || reprocessing) ? 'not-allowed' : 'pointer',
                      opacity: (resendingAll || reprocessing) ? 0.6 : 1,
                    }}
                    title="Resend all expense lines to ORDS one by one"
                  >
                    {resendingAll ? '⟳ Resending…' : '↺ Resend All Lines'}
                  </button>
                  {resendAllErr && (
                    <span style={{ fontSize: 10, color: 'var(--red)' }}>{resendAllErr}</span>
                  )}
                </div>

                {/* Nudge — shown for FAILED, or COMPLETE with missing lines */}
                {canNudge && !hasMismatch && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                    <button
                      disabled={nudging || reprocessing || resendingAll}
                      onClick={handleNudge}
                      style={{
                        padding: '3px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                        border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.1)',
                        color: '#f59e0b',
                        cursor: (nudging || reprocessing || resendingAll) ? 'not-allowed' : 'pointer',
                        opacity: (nudging || reprocessing || resendingAll) ? 0.6 : 1,
                      }}
                      title="Resume from where the batch got stuck — skips already-processed expenditures"
                    >
                      {nudging ? '⟳ Nudging…' : '⚡ Nudge (Resume)'}
                    </button>
                    {nudgeMsg && !hasMismatch && (
                      <span style={{
                        fontSize: 10,
                        color: nudgeMsg.type === 'error' ? 'var(--red)'
                             : nudgeMsg.type === 'warn'  ? '#f59e0b'
                             : 'var(--text-muted)',
                      }}>
                        {nudgeMsg.text}
                      </span>
                    )}
                  </div>
                )}

                {/* Reprocess batch (re-runs matching) */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                  <button
                    disabled={reprocessing || resendingAll}
                    onClick={handleReprocessBatch}
                    style={{
                      padding: '3px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                      border: '1px solid var(--orange)', background: 'rgba(251,146,60,0.1)',
                      color: 'var(--orange)',
                      cursor: (reprocessing || resendingAll) ? 'not-allowed' : 'pointer',
                      opacity: (reprocessing || resendingAll) ? 0.6 : 1,
                    }}
                    title="Re-run AI matching for all lines (clears existing results)"
                  >
                    {reprocessing ? '⟳ Starting…' : '↺ Reprocess Batch'}
                  </button>
                  {reprocessErr && (
                    <span style={{ fontSize: 10, color: 'var(--red)' }}>{reprocessErr}</span>
                  )}
                </div>
              </div>
            </div>
          )}
          <ScoreLegend />
        </div>
      </div>

      {/* Results view */}
      {view === 'results' && (
        <div style={{ padding: '0 12px' }}>
          {loading && (
            <div className="processing-indicator" style={{ padding: 16 }}>
              <div className="spinner" /><span>Loading results…</span>
            </div>
          )}
          {error && (
            <div className="error-box" style={{ padding: '8px 12px', fontSize: 12 }}>{error}</div>
          )}
          {results && (
            <>
              {(() => {
                const totalCandidates = results.candidate_count
                  ?? (results.results?.[0]?.candidate_count ?? 0);
                return (
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, alignItems: 'center' }}>
                    <span style={{ color: hasMismatch ? '#f59e0b' : undefined, fontWeight: hasMismatch ? 600 : undefined }}>
                      {results.lines_complete} of {results.lines_total} lines complete
                      {hasMismatch && <span style={{ marginLeft: 5 }}>⚠ mismatch</span>}
                    </span>
                    {totalCandidates > 0 && (
                      <span>{totalCandidates.toLocaleString()} total candidates</span>
                    )}
                    {results.matching_mode && <span>Mode: <strong>{results.matching_mode.toUpperCase()}</strong></span>}
                    {results.completedAt && <span>Completed {timeAgo(results.completedAt)}</span>}
                  </div>
                );
              })()}

              {/* Missing lines banner — only for COMPLETE batches with a count gap */}
              {hasMismatch && results.missing_lines?.length > 0 && (
                <div style={{
                  marginBottom: 10, padding: '8px 12px', borderRadius: 6,
                  background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b' }}>
                      ⚠ {results.missing_lines.length} line{results.missing_lines.length !== 1 ? 's' : ''} missing from results
                    </span>
                    <button
                      disabled={nudging || nudgePolling}
                      onClick={handleNudge}
                      style={{
                        padding: '2px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                        border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.15)',
                        color: '#f59e0b', cursor: (nudging || nudgePolling) ? 'not-allowed' : 'pointer',
                        opacity: (nudging || nudgePolling) ? 0.6 : 1,
                      }}
                      title="Process the missing lines and add them to results"
                    >
                      {nudging ? '⟳ Starting…' : nudgePolling ? '⟳ Processing…' : '⚡ Nudge to fix'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {results.missing_lines.map(ml => (
                      <div key={ml.exp_id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 11 }}>
                        <span className="mono" style={{ fontWeight: 700, color: 'var(--accent-hover)', minWidth: 130, flexShrink: 0 }}>
                          {ml.exp_id}
                        </span>
                        {ml.amount != null && (
                          <span style={{ color: 'var(--accent-hover)', fontWeight: 600, minWidth: 60, flexShrink: 0 }}>
                            ${Number(ml.amount).toLocaleString()}
                          </span>
                        )}
                        <span style={{ color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ml.exp_cat || ml.exp_txn_desc || '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                  {nudgeMsg && (
                    <div style={{ marginTop: 6, fontSize: 10, color: nudgeMsg.type === 'error' ? 'var(--red)' : nudgeMsg.type === 'warn' ? '#f59e0b' : 'var(--text-muted)' }}>
                      {nudgeMsg.text}
                    </div>
                  )}
                </div>
              )}

              {results.results?.length > 0 ? (() => {
                const allLines   = results.results;
                const totalLines = allLines.length;
                const pageStart  = expPage * expPageSize;
                const pagedLines = allLines.slice(pageStart, pageStart + expPageSize);
                return (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                    {pagedLines.map((lr, i) => (
                      <ExpRow
                        key={lr.expenditure.exp_id}
                        lineResult={lr}
                        lineNumber={pageStart + i + 1}
                        batchId={batch.batch_id}
                        batchStatus={batch.status}
                        mode={results.matching_mode}
                        onReprocess={d => setResults(d)}
                      />
                    ))}
                    <Paginator
                      total={totalLines}
                      page={expPage}
                      pageSize={expPageSize}
                      onPage={setExpPage}
                      onPageSize={s => { setExpPageSize(s); setExpPage(0); }}
                    />
                  </div>
                );
              })() : (
                <div className="empty-state" style={{ padding: 16 }}>
                  {['LINE_PROCESSING', 'PROCESSING', 'WIP', 'FETCHING_DATA'].includes(batch.status)
                    ? 'Processing in progress — results will appear as each line completes…'
                    : 'No results yet.'}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Usage view */}
      {view === 'usage' && (
        <div style={{ padding: '0 16px' }}>
          {!results && !loading && (
            <div className="empty-state" style={{ padding: 16 }}>No usage data — load results first.</div>
          )}
          {loading && (
            <div className="processing-indicator" style={{ padding: 16 }}>
              <div className="spinner" /><span>Loading…</span>
            </div>
          )}
          {results && (() => {
            const mode = results.matching_mode;
            const lu   = results.llm_usage;
            const vu   = results.vector_usage;
            const hasAny = lu || vu || results.processing_time_ms;

            if (!hasAny) {
              return (
                <div className="empty-state" style={{ padding: 16 }}>
                  No usage data available.{' '}
                  {['NEW','WIP','FETCHING_DATA','PROCESSING','LINE_PROCESSING'].includes(batch.status)
                    ? 'Batch is still processing.'
                    : 'Usage is recorded when the batch completes.'}
                </div>
              );
            }

            const KV = ({ label, value }) => value == null ? null : (
              <div style={{ display: 'flex', gap: 8, fontSize: 12, padding: '3px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ color: 'var(--text-muted)', minWidth: 160 }}>{label}</span>
                <span className="mono" style={{ fontWeight: 600 }}>{value}</span>
              </div>
            );

            const Section = ({ title, children }) => (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
                  textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                  {title}
                </div>
                {children}
              </div>
            );

            return (
              <div style={{ maxWidth: 480, paddingTop: 8 }}>
                <Section title="General">
                  <KV label="Mode" value={mode?.toUpperCase()} />
                  <KV label="Processing Time" value={results.processing_time_ms ? `${results.processing_time_ms.toLocaleString()}ms` : null} />
                  <KV label="Lines Processed" value={`${results.lines_complete} / ${results.lines_total}`} />
                </Section>

                {lu && (
                  <Section title="LLM Usage">
                    <KV label="Model"             value={lu.model} />
                    <KV label="Endpoint"          value={lu.litellm_base_url} />
                    <KV label="Prompt Tokens"     value={lu.prompt_tokens?.toLocaleString()} />
                    <KV label="Completion Tokens" value={lu.completion_tokens?.toLocaleString()} />
                    <KV label="Total Tokens"      value={lu.total_tokens?.toLocaleString()} />
                    {lu.total_cost_usd != null && (
                      <>
                        <div style={{
                          marginTop: 8, marginBottom: 4,
                          fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
                          textTransform: 'uppercase', letterSpacing: 1,
                        }}>
                          Estimated Cost (Vertex AI)
                        </div>
                        <KV label="Prompt Cost"     value={`$${lu.prompt_cost_usd?.toFixed(6)}`} />
                        <KV label="Completion Cost" value={`$${lu.completion_cost_usd?.toFixed(6)}`} />
                        <div style={{
                          display: 'flex', gap: 8, fontSize: 12, padding: '5px 0',
                          borderBottom: '2px solid var(--border)',
                        }}>
                          <span style={{ color: 'var(--text-muted)', minWidth: 160, fontWeight: 700 }}>Total Cost</span>
                          <span className="mono" style={{ fontWeight: 800, color: 'var(--green)', fontSize: 13 }}>
                            ${lu.total_cost_usd?.toFixed(6)}
                            <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>
                              USD{lu.pricing_tier === 'long-context' ? ' · long-context tier' : ''}
                            </span>
                          </span>
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                          Based on Vertex AI list pricing. Verify at cloud.google.com/vertex-ai/generative-ai/pricing
                        </div>
                      </>
                    )}
                  </Section>
                )}

                {vu && (
                  <Section title="Vector / Embedding Usage">
                    <KV label="Embedding Model"      value={vu.embedding_model} />
                    <KV label="Candidates Embedded"  value={vu.candidates_embedded?.toLocaleString()} />
                    <KV label="Dimensions"           value={vu.dimensions?.toLocaleString()} />
                    <KV label="Embedding Tokens"     value={vu.total_tokens?.toLocaleString()} />
                  </Section>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* History view */}
      {view === 'history' && (
        <div style={{ padding: '0 16px' }}>
          {batch.error_message && (
            <div style={{
              margin: '0 0 10px', padding: '8px 12px', borderRadius: 6,
              background: 'var(--red-bg)', borderLeft: '3px solid var(--red)',
              color: 'var(--red)', fontSize: 12,
            }}>
              <strong>Error:</strong> {batch.error_message}
            </div>
          )}
          {(batch.state_history || []).map((h, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
              <div style={{
                marginTop: 3, width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: STATUS_COLORS[h.status] === 'failed'     ? 'var(--red)'
                          : STATUS_COLORS[h.status] === 'completed'  ? 'var(--green)'
                          : STATUS_COLORS[h.status] === 'processing' ? 'var(--orange)'
                          : 'var(--blue)',
              }} />
              <div>
                <span style={{ fontSize: 11, fontWeight: 600, marginRight: 6 }}>
                  {STATUS_LABELS[h.status] || h.status}
                </span>
                <span style={{ fontSize: 10, color: 'var(--text-muted)', marginRight: 6 }}>
                  {h.timestamp ? new Date(h.timestamp).toLocaleString() : ''}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{h.message}</span>
              </div>
            </div>
          ))}
          {(!batch.state_history || batch.state_history.length === 0) && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>No history available</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main BatchList component ──────────────────────────────────────────────────
export default function BatchList() {
  const [batches, setBatches]           = useState([]);
  const [page, setPage]                 = useState(1);
  const [pages, setPages]               = useState(1);
  const [total, setTotal]               = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [pageSize, setPageSize]         = useState(10);
  const [expanded, setExpanded]         = useState(null);
  const [reprocessing, setReprocessing] = useState({});
  const [nudging, setNudging]           = useState({});
  const [deleting, setDeleting]         = useState({});
  const [errors, setErrors]             = useState({});

  useEffect(() => { setPage(1); }, [statusFilter, pageSize]);

  const load = useCallback(() => {
    getBatches(page, pageSize, statusFilter)
      .then(data => {
        setBatches(data.batches);
        setPages(data.pages);
        setTotal(data.total);
      })
      .catch(console.error);
  }, [page, pageSize, statusFilter]);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const handleDelete = async (batchId, e) => {
    e.stopPropagation();
    if (!window.confirm(`[DEV] Delete batch ${batchId}? This lets you re-submit the same batch_id.`)) return;
    setDeleting(prev => ({ ...prev, [batchId]: true }));
    setErrors(prev => ({ ...prev, [batchId]: null }));
    try {
      await deleteBatch(batchId);
      if (expanded === batchId) setExpanded(null);
      load();
    } catch (err) {
      setErrors(prev => ({ ...prev, [batchId]: err.response?.data?.error || err.message }));
      setDeleting(prev => ({ ...prev, [batchId]: false }));
    }
  };

  const handleReprocess = async (batchId, e) => {
    e.stopPropagation();
    setReprocessing(prev => ({ ...prev, [batchId]: true }));
    setErrors(prev => ({ ...prev, [batchId]: null }));
    try {
      await reprocessBatch(batchId);
      load();
    } catch (err) {
      setErrors(prev => ({ ...prev, [batchId]: err.response?.data?.error || err.message }));
    } finally {
      setReprocessing(prev => ({ ...prev, [batchId]: false }));
    }
  };

  const handleNudge = async (batchId, e) => {
    e.stopPropagation();
    setNudging(prev => ({ ...prev, [batchId]: true }));
    setErrors(prev => ({ ...prev, [batchId]: null }));
    try {
      await nudgeBatchApi(batchId);
      load();
    } catch (err) {
      const status = err.response?.status;
      const msg    = err.response?.data?.error || err.message;
      setErrors(prev => ({
        ...prev,
        [batchId]: status === 409 ? `Already running — ${msg}` : msg,
      }));
    } finally {
      setNudging(prev => ({ ...prev, [batchId]: false }));
    }
  };

  return (
    <div className="request-list-panel">
      {/* Toolbar */}
      <div className="list-toolbar">
        <div className="toolbar-left">
          <select className="state-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} aria-label="Filter by status">
            <option value="">All Statuses</option>
            <option value="NEW">New</option>
            <option value="WIP">WIP</option>
            <option value="FETCHING_DATA">Fetching Data</option>
            <option value="LINE_PROCESSING">Matching</option>
            <option value="COMPLETE">Complete</option>
            <option value="FAILED">Failed</option>
          </select>
          <span className="total-count">{total} batch{total !== 1 ? 'es' : ''}</span>
        </div>
        <div className="toolbar-right">
          <select className="state-select" value={pageSize} onChange={e => setPageSize(Number(e.target.value))} aria-label="Rows per page" style={{ width: 'auto', padding: '3px 6px' }}>
            <option value={5}>5 / page</option>
            <option value={10}>10 / page</option>
            <option value={25}>25 / page</option>
          </select>
          <nav aria-label="Batch list pagination" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button className="page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)} aria-label="Previous page">Prev</button>
            <span className="page-info" aria-live="polite" aria-atomic="true">{page}/{pages}</span>
            <button className="page-btn" disabled={page >= pages} onClick={() => setPage(p => p + 1)} aria-label="Next page">Next</button>
          </nav>
        </div>
      </div>

      {/* Table */}
      <div className="data-table-wrap">
        <table className="data-table compact">
          <thead>
            <tr>
              <th></th>
              <th>Batch ID</th>
              <th>Name</th>
              <th>Status</th>
              <th>Exp</th>
              <th>Cands</th>
              <th>Received</th>
              <th>Last Update</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {batches.map(b => {
              const colorClass   = STATUS_COLORS[b.status] || 'received';
              const lastHistory  = b.state_history?.[b.state_history.length - 1];
              const isExpanded   = expanded === b.batch_id;
              const isRetrying   = reprocessing[b.batch_id];
              const isNudging    = nudging[b.batch_id];
              const isDeleting   = deleting[b.batch_id];
              const canReprocess    = b.status === 'FAILED' || b.status === 'COMPLETE';
              const rowMismatch     = b.status === 'COMPLETE'
                && b.expenditure_count > 0
                && (b.line_result_count ?? b.expenditure_count) < b.expenditure_count;
              const rowCanNudge     = ['LINE_PROCESSING', 'PROCESSING', 'PAUSED', 'FAILED'].includes(b.status) || rowMismatch;
              const expCount        = b.expenditure_count ?? null;
              const candCount       = b.candidate_count ?? null;
              const linesDone       = b.line_result_count ?? null;

              return (
                <React.Fragment key={b.batch_id}>
                  <tr
                    style={{ cursor: 'pointer' }}
                    onClick={() => setExpanded(prev => prev === b.batch_id ? null : b.batch_id)}
                  >
                    <td style={{ width: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 10 }}>
                      {isExpanded ? '▼' : '▶'}
                    </td>
                    <td className="mono">{b.batch_id}</td>
                    <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.batch_name || '-'}
                    </td>
                    <td>
                      <span className={`badge ${colorClass}`}>
                        {STATUS_LABELS[b.status] || b.status}
                      </span>
                      {rowMismatch && (
                        <span
                          title={`Only ${linesDone} of ${expCount} lines have results — click Nudge to fix`}
                          style={{ marginLeft: 5, fontSize: 10, color: '#f59e0b', fontWeight: 700, cursor: 'default' }}
                        >
                          ⚠
                        </span>
                      )}
                    </td>
                    <td style={{ fontSize: 11, textAlign: 'right' }}>
                      {expCount != null ? (
                        rowMismatch ? (
                          <span style={{ color: '#f59e0b', fontWeight: 700 }} title={`${linesDone} of ${expCount} lines have results`}>
                            {linesDone}/{expCount}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>{expCount}</span>
                        )
                      ) : '—'}
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right' }}>
                      {candCount != null ? candCount : '—'}
                    </td>
                    <td className="mono" style={{ fontSize: 11, whiteSpace: 'nowrap' }} title={b.createdAt}>
                      {b.createdAt ? fmtDateTime(b.createdAt) : '—'}
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 200,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={lastHistory?.message || ''}>
                      {lastHistory?.message || '—'}
                    </td>
                    <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                      {canReprocess && (
                        <button
                          disabled={isRetrying}
                          style={{
                            padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                            border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.1)',
                            color: '#ef4444', cursor: isRetrying ? 'not-allowed' : 'pointer',
                            opacity: isRetrying ? 0.6 : 1,
                          }}
                          onClick={e => handleReprocess(b.batch_id, e)}
                        >
                          {isRetrying ? 'Starting…' : 'Reprocess'}
                        </button>
                      )}
                      {rowCanNudge && (
                        <button
                          disabled={isNudging}
                          title="Resume processing from the last completed expenditure"
                          style={{
                            padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                            border: '1px solid rgba(245,158,11,0.5)', background: 'rgba(245,158,11,0.1)',
                            color: '#f59e0b', cursor: isNudging ? 'not-allowed' : 'pointer',
                            opacity: isNudging ? 0.6 : 1, marginLeft: 6,
                          }}
                          onClick={e => handleNudge(b.batch_id, e)}
                        >
                          {isNudging ? '⟳ …' : '⚡ Nudge'}
                        </button>
                      )}
                      <button
                        disabled={isDeleting}
                        title="DEV: delete so you can re-submit the same batch_id"
                        style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                          border: '1px solid rgba(251,191,36,0.4)', background: 'rgba(251,191,36,0.1)',
                          color: '#fbbf24', cursor: isDeleting ? 'not-allowed' : 'pointer',
                          opacity: isDeleting ? 0.6 : 1, marginLeft: 6,
                        }}
                        onClick={e => handleDelete(b.batch_id, e)}
                      >
                        {isDeleting ? '…' : 'DEV: Del'}
                      </button>
                      {errors[b.batch_id] && (
                        <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--red)' }}>
                          {errors[b.batch_id]}
                        </span>
                      )}
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr>
                      <td colSpan={9} style={{ padding: 0 }}>
                        <BatchDetail batch={b} onReprocess={handleReprocess} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {batches.length === 0 && (
              <tr>
                <td colSpan={9} className="empty-state" style={{ padding: 24, textAlign: 'center' }}>
                  No batches found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="kv-row">
      <span className="kv-label">{label}</span>
      <span className="kv-value">{value}</span>
    </div>
  );
}

function Paginator({ total, page, pageSize, onPage, onPageSize }) {
  const totalPages = Math.ceil(total / pageSize) || 1;
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to   = Math.min((page + 1) * pageSize, total);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
      fontSize: 11, color: 'var(--text-muted)',
      borderTop: '1px solid var(--border)', flexWrap: 'wrap',
      background: 'var(--bg-card)',
    }}>
      <span>Rows per page:</span>
      {[10, 25].map(size => (
        <button
          key={size}
          onClick={() => { onPageSize(size); onPage(0); }}
          style={{
            padding: '1px 9px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
            border: '1px solid var(--border)',
            background: pageSize === size ? 'var(--accent)' : 'transparent',
            color: pageSize === size ? '#fff' : 'var(--text-muted)',
            fontWeight: pageSize === size ? 700 : 400,
          }}
        >{size}</button>
      ))}
      <span style={{ marginLeft: 6 }}>{from}–{to} of {total}</span>
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
        <button
          disabled={page === 0}
          onClick={() => onPage(page - 1)}
          style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', fontSize: 12, cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.4 : 1 }}
        >‹ Prev</button>
        <span style={{ fontSize: 11, padding: '0 4px' }}>{page + 1} / {totalPages}</span>
        <button
          disabled={page >= totalPages - 1}
          onClick={() => onPage(page + 1)}
          style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', fontSize: 12, cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1 }}
        >Next ›</button>
      </div>
    </div>
  );
}
